import React from "react";
import { Button, Modal } from "components/ui/BootstrapCompat";
import { FiArrowDown, FiArrowLeft, FiArrowRight, FiPlus, FiSliders, FiTarget, FiTrash2 } from "react-icons/fi";
import type { AlertImageUploadField, FlowContextMenuState, FlowEditorNode, FlowEditorState, FlowNodeKind, WidgetAnchor } from "./types";
import { RGL_COLS, RGL_CONTAINER_PADDING, RGL_MARGIN, RGL_ROW_HEIGHT, enabledFlowNodePalette, resizeHandles, tabs, viewportPresets } from "./constants";
import { getEditorNodePixels, getEffectiveNodeAnchor, getFlowViewport, getGridConfig, getGridItemPixels, getGridRows, getNodeGridItem, isNodeAnchored } from "./flowUtils";
import { FlowNodePreview } from "./PublicPreview";
import { FlowNodeSettingsPanel } from "./FlowNodeSettingsPanel";

type ImageUploadState = { nodeId: string; field: AlertImageUploadField; progress: number; error: string } | null;
type FlowBuilderViewProps = {
  canvasRef: React.RefObject<HTMLDivElement | null>; stageRef: React.RefObject<HTMLDivElement | null>;
  flowState: FlowEditorState; selectedNodeId: string; viewport: FlowEditorState["viewport"]; stageGridSize: string;
  isAddObjectOpen: boolean; settingsNode?: FlowEditorNode; contextMenu: FlowContextMenuState; imageUploadState: ImageUploadState;
  handleDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  handleNodePointerDown: (event: React.PointerEvent<HTMLDivElement>, node: FlowEditorNode) => void;
  handleNodePointerMove: (event: React.PointerEvent<HTMLDivElement>, node: FlowEditorNode) => void;
  handleNodePointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
  handleNodePointerCancel: (event: React.PointerEvent<HTMLDivElement>) => void;
  handleNodeResizePointerDown: (event: React.PointerEvent<HTMLButtonElement>, node: FlowEditorNode, handle: any) => void;
  handleNodeContextMenu: (event: React.MouseEvent<HTMLDivElement>, node: FlowEditorNode) => void;
  addNode: (kind: FlowNodeKind, position?: { x: number; y: number }) => void; deleteNode: (nodeId: string) => void;
  setIsAddObjectOpen: (isOpen: boolean) => void; setSettingsNodeId: (nodeId: string) => void; setContextMenu: (state: FlowContextMenuState) => void;
  applyAlertLayoutPreset: (nodeId: string, preset: any) => void; clearImageUploadState: (nodeId: string, field: AlertImageUploadField) => void;
  setNodeAnchor: (nodeId: string, anchor: WidgetAnchor) => void; updateNodeData: (nodeId: string, patch: Partial<FlowEditorNode["data"]>) => void;
  updateNodeSetting: (nodeId: string, key: string, value: any) => void; uploadNodeAlertImage: (node: FlowEditorNode, file: File, field: AlertImageUploadField) => void;
  onSelectNode: (nodeId: string) => void;
};

export function FlowBuilderView({
  canvasRef, stageRef, flowState, selectedNodeId, viewport, stageGridSize,
  isAddObjectOpen, settingsNode, contextMenu, imageUploadState,
  handleDrop, handleNodePointerDown, handleNodePointerMove, handleNodePointerUp,
  handleNodePointerCancel, handleNodeResizePointerDown, handleNodeContextMenu,
  addNode, deleteNode, setIsAddObjectOpen, setSettingsNodeId, setContextMenu,
  applyAlertLayoutPreset, clearImageUploadState, setNodeAnchor, updateNodeData,
  updateNodeSetting, uploadNodeAlertImage, onSelectNode,
}: FlowBuilderViewProps) {
  return (
    <div className="relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-[#c9d8e8] bg-[#f8fafc] shadow-[0_14px_34px_rgba(16,24,40,0.10)]">
      <div className="flex flex-none flex-col gap-2 border-b border-[#dbe4ef] bg-white p-2 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Button
            variant="primary"
            className="inline-flex h-9 flex-1 items-center gap-2 rounded-lg px-3 text-xs font-bold sm:flex-none"
            onClick={() => setIsAddObjectOpen(true)}
          >
            <FiPlus className="h-4 w-4" />
            ເພີ່ມ Object
          </Button>
        </div>
        <div className="min-w-0 truncate text-xs font-bold text-[#667085]">
          {viewport.width} x {viewport.height} px ·{" "}
          {Math.round(viewport.zoom * 100)}% · {flowState.nodes.length} object
        </div>
      </div>

      <div
        ref={canvasRef}
        className="relative min-h-0 min-w-0 flex-1 overflow-auto bg-[#dbe9f8] p-3 sm:p-6"
        onClick={() => {
          onSelectNode("");
          setContextMenu(null);
        }}
        onContextMenu={(event) => event.preventDefault()}
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
      >
        <div className="grid min-h-full min-w-full place-items-center">
          <div
            className="relative"
            style={{
              width: viewport.width * viewport.zoom,
              height: viewport.height * viewport.zoom,
            }}
          >
            <div
              ref={stageRef}
              className="absolute left-0 top-0 overflow-hidden rounded-lg border border-[#98bfe4] bg-[#f8fbff] shadow-[0_18px_45px_rgba(16,24,40,0.16)]"
              style={{
                width: viewport.width,
                height: viewport.height,
                transform: `scale(${viewport.zoom})`,
                transformOrigin: "top left",
                backgroundImage:
                  "linear-gradient(to right, rgba(22,143,235,0.22) 1px, transparent 1px), linear-gradient(to bottom, rgba(22,143,235,0.22) 1px, transparent 1px)",
                backgroundPosition: `${RGL_CONTAINER_PADDING[0]}px ${RGL_CONTAINER_PADDING[1]}px`,
                backgroundSize: stageGridSize,
              }}
            >
              {flowState.nodes.map((node) => {
                const isSelected = selectedNodeId === node.id;
                const gridItem = getNodeGridItem(node, viewport);
                const pixels = getEditorNodePixels(node, viewport, gridItem);

                return (
                  <div
                    key={node.id}
                    className="absolute select-none rounded-lg transition"
                    style={{
                      left: pixels.left,
                      top: pixels.top,
                      width: pixels.width,
                      height: pixels.height,
                      cursor: isNodeAnchored(node) ? "default" : "grab",
                      outline: isSelected
                        ? "2px solid #168feb"
                        : "1px dashed rgba(22, 143, 235, 0.32)",
                      outlineOffset: isSelected ? 3 : 2,
                      boxShadow: isSelected
                        ? "0 0 0 4px rgba(22, 143, 235, 0.14)"
                        : undefined,
                    }}
                    title={node.data.label}
                    onClick={(event) => event.stopPropagation()}
                    onContextMenu={(event) =>
                      handleNodeContextMenu(event, node)
                    }
                    onPointerDown={(event) =>
                      handleNodePointerDown(event, node)
                    }
                    onPointerMove={(event) =>
                      handleNodePointerMove(event, node)
                    }
                    onPointerUp={handleNodePointerUp}
                    onPointerCancel={handleNodePointerCancel}
                  >
                    <FlowNodePreview node={node} />
                    {isSelected &&
                      resizeHandles.map((item) => (
                        <button
                          key={item.handle}
                          type="button"
                          aria-label={item.label}
                          title={item.label}
                          className={`absolute z-10 border border-[#168feb] bg-white shadow-[0_6px_14px_rgba(22,143,235,0.28)] transition hover:scale-110 ${item.className}`}
                          onClick={(event) => event.stopPropagation()}
                          onPointerDown={(event) =>
                            handleNodeResizePointerDown(
                              event,
                              node,
                              item.handle,
                            )
                          }
                        />
                      ))}
                  </div>
                );
              })}

              {!flowState.nodes.length && (
                <div className="absolute inset-0 grid place-items-center text-sm font-black text-[#98a2b3]">
                  Empty canvas
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {contextMenu && (
        <div
          className="fixed z-[1060] w-44 overflow-hidden rounded-lg border border-[#dbe4ef] bg-white py-1 shadow-[0_18px_45px_rgba(16,24,40,0.18)]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="flex h-9 w-full items-center gap-2 px-3 text-left text-xs font-black text-[#344054] hover:bg-[#eef7ff] hover:text-[#168feb]"
            onClick={() => {
              setSettingsNodeId(contextMenu.nodeId);
              setContextMenu(null);
            }}
          >
            <FiSliders className="h-4 w-4" />
            ຕັ້ງຄ່າ
          </button>
          <button
            type="button"
            className="flex h-9 w-full items-center gap-2 px-3 text-left text-xs font-black text-[#344054] hover:bg-[#eef7ff] hover:text-[#168feb]"
            onClick={() => {
              setNodeAnchor(contextMenu.nodeId, "center");
              setContextMenu(null);
            }}
          >
            <FiTarget className="h-4 w-4" />
            Center
          </button>
          <button
            type="button"
            className="flex h-9 w-full items-center gap-2 px-3 text-left text-xs font-black text-[#344054] hover:bg-[#eef7ff] hover:text-[#168feb]"
            onClick={() => {
              setNodeAnchor(contextMenu.nodeId, "bottom");
              setContextMenu(null);
            }}
          >
            <FiArrowDown className="h-4 w-4" />
            Bottom
          </button>
          <button
            type="button"
            className="flex h-9 w-full items-center gap-2 px-3 text-left text-xs font-black text-[#b42318] hover:bg-[#fff1f0]"
            onClick={() => deleteNode(contextMenu.nodeId)}
          >
            <FiTrash2 className="h-4 w-4" />
            ລຶບ
          </button>
        </div>
      )}

      <Modal
        show={isAddObjectOpen}
        onHide={() => setIsAddObjectOpen(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="flex items-center gap-2">
              <FiPlus className="h-4 w-4" />
              ເພີ່ມ Object
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {enabledFlowNodePalette.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.kind}
                  type="button"
                  className="flex min-h-[74px] items-center gap-3 rounded-lg border border-[#dbe4ef] bg-[#f8fafc] p-3 text-left transition hover:border-[#168feb] hover:bg-[#eef7ff]"
                  onClick={() => {
                    addNode(item.kind);
                    setIsAddObjectOpen(false);
                  }}
                >
                  <span
                    className="grid h-10 w-10 flex-none place-items-center rounded-lg text-white"
                    style={{ backgroundColor: item.color }}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-black text-[#101828]">
                      {item.label}
                    </span>
                    <span className="mt-0.5 block text-xs font-medium capitalize text-[#667085]">
                      {item.kind}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(settingsNode)}
        onHide={() => setSettingsNodeId("")}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="flex min-w-0 items-center gap-2">
              <FiSliders className="h-4 w-4 flex-none" />
              <span className="truncate">
                ຕັ້ງຄ່າ {settingsNode?.data.label || "Object"}
              </span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(100vh-210px)] overflow-auto">
          {settingsNode && (
            <FlowNodeSettingsPanel
              node={settingsNode}
              imageUploadState={imageUploadState}
              applyAlertLayoutPreset={applyAlertLayoutPreset}
              clearImageUploadState={clearImageUploadState}
              setNodeAnchor={setNodeAnchor}
              updateNodeData={updateNodeData}
              updateNodeSetting={updateNodeSetting}
              uploadNodeAlertImage={uploadNodeAlertImage}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          {settingsNode && (
            <Button
              variant="outline-danger"
              className="inline-flex h-9 items-center gap-2 rounded-lg px-3 text-xs font-bold"
              onClick={() => deleteNode(settingsNode.id)}
            >
              <FiTrash2 className="h-4 w-4" />
              ລຶບ
            </Button>
          )}
          <Button
            variant="primary"
            className="h-9 rounded-lg px-4 text-xs font-bold"
            onClick={() => setSettingsNodeId("")}
          >
            ປິດ
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
